import React, { useState, useMemo, useEffect } from 'react';
import DefaultLayout from 'admin/components/DefaultLayout';
import { useIntl } from 'react-intl';
import {
  Button,
  Tabs,
  Table,
  DatePicker,
  Dropdown,
  Input,
  InputNumber,
  Modal,
} from 'ui';
import Filter from 'admin/components/Filter';
import { StyledButtonList } from 'styles/buttonList';
import dayjs, { Dayjs } from 'dayjs';
import EnumSelect from '../../../Selects/EnumSelect';
import useQueryApiClient from '../../../../utils/useQueryApiClient';
import FileDownload from 'js-file-download';
import { MenuProps } from 'antd/es/menu';
import { DownOutlined, UpOutlined } from '@ant-design/icons';
import { Pagination } from '../../../../types/Pagination';
import { StyledPage } from './style';
import type { MenuInfo } from 'rc-menu/lib/interface';
import useFarmOrderHandler from 'hooks/useFarmOrderHandler';
import { OrdersViewOrder } from '../../Orders/OrdersViewOrder/OrdersViewOrder';
import { message } from 'antd';
import { useAuth } from '../../../../hooks/useAuth';
import { useSearchParams } from 'react-router-dom';

interface transaction {
  status_not: string[];
  year?: string;
  month?: string | number;
  sort?: any;
}

const ACTION_KEYS = {
  FARM_ORDER: 'farm_order',
  CONFIRMED: 'confirmed',
  SENT: 'sent',
  DELIVERED: 'delivered',
  CANCELED: 'canceled',
};

const DEFAULT_STATUSES = [
  ACTION_KEYS.CONFIRMED,
  ACTION_KEYS.DELIVERED,
  ACTION_KEYS.SENT,
];
const TransactionListPage = () => {
  const [advancedSearch, setAdvancedSearch] = useState<transaction>({
    status_not: ['canceled', 'delivered'],
    year: dayjs().locale('lv').format('YYYY'),
    month: dayjs().month() + 1,
    sort: { created_at: 'desc' },
  });

  const { handleDownloadClick } = useFarmOrderHandler();

  const [selectedTab, setSelectedTab] = useState<string>('active_transactions');
  const [isOnStatusChange, setIsOnStatusChange] = useState<boolean>(false);
  const [reloadPage, setReloadPage] = useState<number>(0);
  const [idsToCancel, setIdsToCancel] = useState<React.Key[]>([]);
  const [viewOrderData, setViewOrderData] = useState();
  const [isOpenViewOrderDrawer, setIsOpenViewOrderDrawer] =
    useState<boolean>(false);
  const [reloadFilter, setReloadFilter] = useState<number>(0);

  const [availableYears, setAvailableYears] = useState<number[]>([]);
  const [availableMonths, setAvailableMonths] = useState<number[]>([]);

  const [paginationStatus, setPaginationStatus] = useState<Pagination>({});
  const [staticFilter, setStaticFilter] = useState<transaction>({
    status_not: ['canceled', 'delivered'],
  });
  const intl = useIntl();
  const { user } = useAuth();

  let [searchParams] = useSearchParams();
  const transactionId = searchParams.get('transaction');

  useEffect(() => {
    if (!transactionId) return;
    
    fetchOrder({}, { transactionId: transactionId });
  }, [transactionId]);

  const isAdmin = user?.isAdmin;

  const [advancedOptionsVisible, setAdvancedOptionsVisible] =
    useState<boolean>(false);

  const handleSuccessOrderFetch = (response: any) => {
    setViewOrderData(response);
    setIsOpenViewOrderDrawer(true);
  };

  const handleCloseViewOrderDrawer = () => {
    setViewOrderData(undefined);
    setIsOpenViewOrderDrawer(false);
  };

  const { appendData: exportData } = useQueryApiClient({
    request: {
      url: `api/v2/farm/transactions/export`,
      data: {
        filter: advancedSearch,
      },
      multipart: true,
      method: 'POST',
    },
    onSuccess: (response, format) => {
      FileDownload(
        response,
        `${format}_${dayjs().format('YYYY-MM-DD')}.${format}`
      );
    },
  });

  const { appendData: changeStatus } = useQueryApiClient({
    request: {
      url: `api/v2/transactions/change-status`,
      method: 'POST',
    },
    onSuccess: () => {
      setIsOnStatusChange(false);
      setReloadPage(reloadPage + 1);
    },
    onError: () => {
      setIsOnStatusChange(false);
    },
  });

  const { appendData: fetchOrder } = useQueryApiClient({
    request: {
      url: 'api/v2/transactions/:transactionId',
      disableOnMount: true,
    },
    onSuccess: (response) => handleSuccessOrderFetch(response),
  });

  const columns = [
    {
      title: (
        <div
          onClick={() =>
            setAdvancedSearch({
              ...advancedSearch,
              sort: {
                created_at:
                  advancedSearch?.sort?.created_at == 'asc' ? 'desc' : 'asc',
              },
            })
          }
        >
          {intl.formatMessage({ id: 'transactions.date_time' })}{' '}
          <span className="sort-arrows">
            <UpOutlined
              className={`sort-arrow sort-arrow-${
                advancedSearch?.sort?.created_at === 'asc'
                  ? 'active'
                  : 'inactive'
              }`}
            />
            <DownOutlined
              className={`sort-arrow sort-arrow-${
                advancedSearch?.sort?.created_at === 'desc'
                  ? 'active'
                  : 'inactive'
              }`}
            />
          </span>
        </div>
      ),
      dataIndex: 'created_date',
      render: (value: string) =>
        value && dayjs(value).format('DD.MM.YYYY | HH:mm'),
    },
    {
      title: intl.formatMessage({ id: 'transactions.order_nr' }),
      dataIndex: 'number',
      render: (value: string) => value || 'Nav numura',
    },
    isAdmin
      ? {
          title: intl.formatMessage({ id: 'transactions.farm_name' }),
          dataIndex: 'brand',
          render: (value: string, record: any) => value ?? record.name ?? '',
        }
      : null,
    {
      title: intl.formatMessage({ id: 'transactions.customer' }),
      dataIndex: 'customer_name',
      render: (value: string) => value,
    },
    {
      title: intl.formatMessage({ id: 'transactions.status' }),
      dataIndex: 'status',
      render: (value: string) =>
        value && intl.formatMessage({ id: 'enum.' + value?.toUpperCase() }),
    },
    {
      title: intl.formatMessage({ id: 'transactions.price' }),
      dataIndex: 'total',
      render: (value: string) => value,
    },
    {
      title: intl.formatMessage({ id: 'transactions.invoice' }),
      dataIndex: 'id',
      render: (values: string, data: { order_id: string; farm_id: string }) => (
        <>
          <span
            className="order-download"
            onClick={() =>
              handleDownloadClick([
                {
                  order_id: data.order_id,
                  farm_id: data.farm_id,
                },
              ])
            }
          >
            <span className="transactions-download">
              {intl.formatMessage({ id: 'transactions.download' })}
            </span>
          </span>
        </>
      ),
    },
    selectedTab === 'active_transactions'
      ? {
          title: intl.formatMessage({ id: 'general.actions' }),
          dataIndex: 'id',
          render: (_: any, record: any) => {
            function handleActionMenuClick(menuInfo: MenuInfo, record: any) {
              if (DEFAULT_STATUSES.includes(menuInfo.key)) {
                changeStatus({
                  id: record.id,
                  status: menuInfo.key,
                });
                return;
              }

              switch (menuInfo.key) {
                case ACTION_KEYS.FARM_ORDER:
                  fetchOrder({}, { transactionId: record.id });
                  break;
                case ACTION_KEYS.CANCELED:
                  setIdsToCancel([record.id]);
                  break;
                default:
                  message.error(
                    intl.formatMessage({ id: 'message.unexpected_error' })
                  );
              }
            }

            const dropdownTableItems: MenuProps['items'] = [
              {
                label: intl.formatMessage({ id: 'general.view' }),
                key: 'farm_order',
              },
              ...(record.order_status !== ACTION_KEYS.CANCELED
                ? [
                    {
                      label: intl.formatMessage({ id: 'enum.SENT' }),
                      key: ACTION_KEYS.SENT,
                    },
                    {
                      label: intl.formatMessage({
                        id: 'transactions.completed',
                      }),
                      key: ACTION_KEYS.DELIVERED,
                    },
                    {
                      label: intl.formatMessage({ id: 'enum.CANCELED' }),
                      key: 'canceled',
                    },
                  ]
                : []),
              ...(record.order_status !== ACTION_KEYS.CONFIRMED
                ? [
                    {
                      label: intl.formatMessage({ id: 'enum.CONFIRMED' }),
                      key: ACTION_KEYS.CONFIRMED,
                    },
                  ]
                : []),
            ];
            const menuPropsTable: MenuProps = {
              items: dropdownTableItems,
              onClick: (info) => handleActionMenuClick(info, record),
            };

            return (
              <Dropdown menu={menuPropsTable}>
                <Button
                  label={intl.formatMessage({ id: 'general.actions' })}
                  icon={<DownOutlined />}
                  iconPosition="end"
                  size="small"
                  className="actions"
                />
              </Dropdown>
            );
          },
        }
      : null,
  ].filter((collum) => collum !== null);

  const { refetch } = useQueryApiClient({
    request: {
      url: `api/v2/farm/transactions/available-dates`,
      data: {
        filter: advancedSearch,
      },
    },
    onSuccess: (response) => {
      setAvailableYears(
        Array.from(new Set(response.map((item: any) => Number(item.year))))
      );
      setAvailableMonths(
        Array.from(new Set(response.map((item: any) => Number(item.month))))
      );
    },
  });

  useEffect(() => {
    refetch();
  }, [selectedTab]);

  const disabledYear = (current: Dayjs) => {
    const year = current.year();

    return !availableYears.includes(year);
  };

  const disabledMonth = (current: Dayjs) => {
    const month = current.month() + 1;

    return !availableMonths.includes(month);
  };

  const items = [
    {
      key: 'active_transactions',
      label: intl.formatMessage({ id: 'transactions.active_transactions' }),
      children: (
        <>
          <Filter
            onFinish={setAdvancedSearch}
            totalRecord={paginationStatus?.total ?? 0}
            staticFilter={staticFilter}
            customParse={{
              year: (value: Dayjs) => value?.year(),
              month: (value: Dayjs) => value?.format('MMMM'),
            }}
            isLabelVisible={true}
            defaultFilterParam={{
              year: dayjs().locale('lv').format('YYYY'),
              month: dayjs().locale('lv').format('MMMM'),
            }}
            reloadFilterTag={reloadFilter}
          >
            <DatePicker
              label={intl.formatMessage({ id: 'general.year' })}
              name="year"
              picker="year"
              format="YYYY"
              disabledDate={disabledYear}
            />
            <DatePicker
              label={intl.formatMessage({ id: 'general.month' })}
              name="month"
              picker="month"
              format="MMMM"
              disabledDate={disabledMonth}
              className="month-picker"
            />
            <EnumSelect
              label={intl.formatMessage({ id: 'transactions.status' })}
              name="status"
              code="PurchaseStatusType"
              limited={['new', 'confirmed', 'sent']}
            />
            <Input
              label={intl.formatMessage({ id: 'transactions.order_nr' })}
              name="order_nr"
            />
            {advancedOptionsVisible && (
              <>
                <Input
                  label={intl.formatMessage({ id: 'transactions.customer' })}
                  name="customer_name"
                />
                <EnumSelect
                  label={intl.formatMessage({
                    id: 'transactions.delivery_type',
                  })}
                  name="delivery_type"
                  code="DeliveryType"
                />
                <InputNumber
                  label={intl.formatMessage({ id: 'transactions.price' })}
                  name="price"
                  precision={2}
                />
              </>
            )}
            <Button
              type="primary"
              label={intl.formatMessage({ id: 'general.select' })}
              htmlType="submit"
            />

            <Button
              label={
                <span>
                  {intl.formatMessage({ id: 'general.advanced_filter' })}{' '}
                  <DownOutlined />
                </span>
              }
              className="advanced_filter"
              onClick={() => setAdvancedOptionsVisible((old) => !old)}
            />
          </Filter>
          <Table
            url="/api/v2/farm/transactions"
            columns={columns}
            filter={advancedSearch}
            paginnationStatus={(el: object) => {
              setPaginationStatus(el);
            }}
            loading={isOnStatusChange}
            reload={reloadPage}
            rowKey={'id'}
          />
        </>
      ),
    },
    {
      key: 'old_transactions',
      label: intl.formatMessage({ id: 'transactions.old_transactions' }),
      children: (
        <>
          <Filter
            onFinish={setAdvancedSearch}
            totalRecord={paginationStatus?.total ?? 0}
            staticFilter={staticFilter}
            customParse={{
              year: (value: Dayjs) => value?.year(),
              month: (value: Dayjs) => value?.format('MMMM'),
            }}
            isLabelVisible={true}
            reloadFilterTag={reloadFilter}
          >
            <DatePicker
              label={intl.formatMessage({ id: 'general.year' })}
              name="year"
              picker="year"
              format="YYYY"
              disabledDate={disabledYear}
            />
            <DatePicker
              label={intl.formatMessage({ id: 'general.month' })}
              name="month"
              picker="month"
              format="MMMM"
              disabledDate={disabledMonth}
              className="month-picker"
            />
            <EnumSelect
              label={intl.formatMessage({ id: 'transactions.status' })}
              name="status"
              code="PurchaseStatusType"
              limited={['canceled', 'delivered']}
            />
            <Input
              label={intl.formatMessage({ id: 'transactions.order_nr' })}
              name="order_nr"
            />
            {advancedOptionsVisible && (
              <>
                <Input
                  label={intl.formatMessage({ id: 'transactions.customer' })}
                  name="customer_name"
                />
                <EnumSelect
                  label={intl.formatMessage({
                    id: 'transactions.delivery_type',
                  })}
                  name="delivery_type"
                  code="DeliveryType"
                />
                <InputNumber
                  label={intl.formatMessage({ id: 'transactions.price' })}
                  name="price"
                  precision={2}
                />
              </>
            )}
            <Button
              type="primary"
              label={intl.formatMessage({ id: 'general.select' })}
              htmlType="submit"
            />

            <Button
              label={
                <span>
                  {intl.formatMessage({ id: 'general.advanced_filter' })}{' '}
                  <DownOutlined />
                </span>
              }
              className="advanced_filter"
              onClick={() => setAdvancedOptionsVisible((old) => !old)}
            />
          </Filter>
          <Table
            url="/api/v2/farm/transactions"
            columns={columns}
            filter={advancedSearch}
            paginnationStatus={(el: object) => {
              setPaginationStatus(el);
            }}
            loading={false}
          />
        </>
      ),
    },
  ];

  const handleMenuClick: MenuProps['onClick'] = (e) => {
    if (e.key === 'xlsx' || e.key === 'csv') {
      exportData({ format: e.key, ...advancedSearch }, [], e.key);
    }
  };

  const dropdownItems: MenuProps['items'] = [
    {
      label: intl.formatMessage({ id: 'general.excel' }),
      key: 'xlsx',
    },
  ];

  const menuProps = {
    items: dropdownItems,
    onClick: handleMenuClick,
  };

  return (
    <DefaultLayout.PageLayout
      breadcrumbs={[
        {
          title: (
            <a href="/">{intl.formatMessage({ id: 'navigation.beginning' })}</a>
          ),
        },
        {
          title: intl.formatMessage({ id: 'navigation.transactions' }),
        },
        {
          title: intl.formatMessage({ id: `transactions.${selectedTab}` }),
        },
      ]}
    >
      <DefaultLayout.PageContent>
        <StyledPage>
          <Tabs
            tabBarExtraContent={
              <StyledButtonList>
                <Dropdown menu={menuProps}>
                  <Button
                    label={intl.formatMessage({ id: 'general.export' })}
                    type="primary"
                  />
                </Dropdown>
              </StyledButtonList>
            }
            items={items}
            onChange={(key) => {
              switch (key) {
                case 'active_transactions':
                  setStaticFilter({
                    status_not: ['canceled', 'delivered'],
                  });
                  setAdvancedSearch({
                    year: dayjs().locale('lv').format('YYYY'),
                    month: dayjs().month() + 1,
                    status_not: ['canceled', 'delivered'],
                    sort: { created_at: 'desc' },
                  });
                  setReloadFilter(reloadFilter + 1);
                  break;
                case 'old_transactions':
                  setStaticFilter({ status_not: ['sent', 'confirmed', 'new'] });
                  setAdvancedSearch({
                    status_not: ['sent', 'confirmed', 'new'],
                    sort: { created_at: 'desc' },
                  });
                  setReloadFilter(reloadFilter + 1);
              }
              setSelectedTab(key);
            }}
          />
          <Modal
            open={idsToCancel.length > 0}
            onCancel={() => setIdsToCancel([])}
            destroyOnClose
            footer={[
              <Button
                key="yes"
                type="primary"
                onClick={() => {
                  setIsOnStatusChange(true);

                  changeStatus({
                    status: ACTION_KEYS.CANCELED,
                    id: idsToCancel[0],
                  });

                  setIdsToCancel([]);
                }}
                label={intl.formatMessage({ id: 'general.yes' })}
              />,
              <Button
                key="no"
                type="primary"
                onClick={() => setIdsToCancel([])}
                label={intl.formatMessage({ id: 'general.no' })}
              />,
            ]}
          >
            <h3>
              {intl.formatMessage({ id: 'general.cancel_purchase_info' })}
            </h3>
            <p>{intl.formatMessage({ id: 'general.transactions_warning' })}</p>
          </Modal>
          <OrdersViewOrder
            open={isOpenViewOrderDrawer}
            onClose={handleCloseViewOrderDrawer}
            transaction={viewOrderData}
            refetchTransaction={fetchOrder}
            showActionButton
          />
        </StyledPage>
      </DefaultLayout.PageContent>
    </DefaultLayout.PageLayout>
  );
};

export default TransactionListPage;
